"use client";

import React from "react";
import { Microscope, ShieldCheck, Settings, Zap, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const certifications = ["Certificazione Biologica", "Efficienza Idrica +50%", "Made in Italy Technology"];
const technicalFeatures = [{
  icon: Microscope,
  title: "Analisi del Suolo",
  desc: "Protocolli avanzati per monitorare l'idratazione profonda delle radici."
}, {
  icon: ShieldCheck,
  title: "Riduzione Evaporazione",
  desc: "Barriera molecolare che trattiene l'umidità nei periodi di siccità."
}, {
  icon: Settings,
  title: "Dosaggio Su Misura",
  desc: "Soluzioni calibrate per le specifiche esigenze delle colture italiane."
}, {
  icon: Zap,
  title: "Risultati Immediati",
  desc: "Cicli di assorbimento rapidi per massimizzare la resa del raccolto."
}];
export default function EngineeringExcellence() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224639" id="solid-dew-rsl" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_40%,transparent_100%)] opacity-20 pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          
          {}
          <div ref={textRef} className={`lg:col-span-5 space-y-10 transition-all duration-1000 ease-out ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 text-primary font-mono text-sm tracking-widest uppercase">
                <span className="w-8 h-px bg-primary"></span>
                Tecnologia Idrica Avanzata
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold tracking-tight text-foreground leading-tight">
                Solid Dew <br className="hidden lg:block" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                  RSL System
                </span>
              </h2>

              <div className="flex flex-wrap gap-2">
                {certifications.map((cert, i) => <Badge key={i} data-index={i} variant="outline" className="bg-accent text-accent-foreground border-primary/20 backdrop-blur-sm font-default text-xs py-1 px-3 rounded-md">
                    {cert}
                  </Badge>)}
              </div>
            </div>

            <p className="text-lg text-muted-foreground text-justify leading-relaxed font-default">
              In Solid Green Italia, l'eccellenza agronomica inizia dalla gestione intelligente dell'acqua. Solid Dew RSL è la nostra soluzione di punta per la ritenzione idrica, progettata per proteggere le colture italiane dallo stress termico. Ogni applicazione garantisce una distribuzione uniforme dell'umidità, riducendo gli sprechi e promuovendo una crescita rigogliosa e sostenibile.
            </p>

            {}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-8 pt-4 border-t border-border/50">
              {technicalFeatures.map((feature, i) => <div key={i} data-index={i} className="flex flex-col gap-3 group">
                  <div className="size-12 rounded-lg bg-card border border-border flex items-center justify-center text-secondary shadow-sm group-hover:border-secondary/50 group-hover:bg-secondary/5 transition-colors duration-300">
                    <feature.icon className="size-6" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-default font-semibold text-foreground text-base mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-snug">
                      {feature.desc}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="pt-6">
              <Button asChild size="lg" className="group bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-lg shadow-md">
                <Link to="/ProductsPage">
                  Scopri i Vantaggi Tecnici
                  <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={imageRef} className={`lg:col-span-7 relative transition-all duration-1000 delay-200 ease-out ${imageVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="relative aspect-[4/3] md:aspect-[16/10] rounded-2xl overflow-hidden border border-border/50 shadow-xl shadow-primary/10 group">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/solid_dew_rsl_packaging_field.png" alt="Confezione Solid Dew RSL in un campo agricolo italiano" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              {}
              <div className="absolute inset-0 bg-gradient-to-tr from-background/40 via-transparent to-transparent mix-blend-multiply" />
              
              {}
              <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
                <div className="bg-card/90 backdrop-blur-md border border-border/50 p-4 rounded-lg shadow-lg">
                  <div className="text-xs font-mono text-secondary mb-1">RISULTATI TEST</div>
                  <div className="font-default font-semibold text-foreground">Efficienza Idrica Certificata</div>
                </div>
              </div>
            </div>

            {}
            <div className="absolute -bottom-12 -left-12 w-3/5 aspect-square rounded-2xl overflow-hidden border-4 border-background shadow-2xl shadow-primary/20 hidden md:block z-20">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/italian_farm_irrigation_detail.png" alt="Dettaglio irrigazione in un'azienda agricola italiana con Solid Dew" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-secondary/10 mix-blend-overlay" />
            </div>
            
            {}
            <div className="absolute -top-6 -right-6 size-24 bg-[radial-gradient(circle,var(--color-secondary)_1px,transparent_1px)] bg-[size:8px_8px] opacity-30 z-0" />
          </div>

        </div>
      </div>
    </section>;
}